import React, { useState, useEffect, useCallback } from 'react';
import { callApi } from './api';
import DeviceRegistry from './DeviceRegistry';
import AdminManagement from './AdminManagement'; // Import the new component
import ActivityLog from './ActivityLog';
import OrphanedUsersDashboard from './OrphanedUsersDashboard';
import AccessRequestsDashboard from './AccessRequestsDashboard';

// Form for creating a new Owner
function CreateOwnerForm({ onOwnerCreated, onCancel, initialType = 'enterprise' }) {
  const [name, setName] = useState('');
  const [orgnr, setOrgnr] = useState('');
  const [type, setType] = useState(initialType);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await callApi('createOwnerV2', { name, orgnr, type });
      if (onOwnerCreated) onOwnerCreated();
    } catch (err) {
      setError('Kunne ikke opprette eier: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px', border: '1px solid #ccc', borderRadius: '5px', margin: '20px auto', display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
      <h4>Opprett Ny {type === 'private' ? 'Privatkunde' : 'Eier'}</h4>
      <div>
        <label htmlFor="create-owner-name" style={{ display: 'block', marginBottom: '5px' }}>Navn ({type === 'private' ? 'Person' : 'Firma/Borettslag'})</label>
        <input id="create-owner-name" value={name} onChange={e => setName(e.target.value)} required />
      </div>
      {type === 'enterprise' && (
        <div>
          <label htmlFor="create-owner-orgnr" style={{ display: 'block', marginBottom: '5px' }}>Organisasjonsnummer (valgfritt)</label>
          <input id="create-owner-orgnr" value={orgnr} onChange={e => setOrgnr(e.target.value)} />
        </div>
      )}
      <div>
        <label htmlFor="create-owner-type" style={{ display: 'block', marginBottom: '5px' }}>Kategori</label>
        <select id="create-owner-type" value={type} onChange={e => setType(e.target.value)}>
          <option value="enterprise">Bedrift / Borettslag (B2B)</option>
          <option value="private">Privatperson (B2C)</option>
        </select>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button type="button" onClick={onCancel}>Avbryt</button>
        <button type="submit" disabled={loading}>{loading ? 'Oppretter...' : 'Opprett'}</button>
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
    </form>
  );
}

// Form for editing an Owner
function EditOwnerForm({ initialData, onFinished }) {
    const [name, setName] = useState(initialData.name || '');
    const [orgnr, setOrgnr] = useState(initialData.orgnr || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await callApi('updateOwnerV2', { ownerId: initialData.id, data: { name, orgnr } });
            if (onFinished) onFinished();
        } catch (err) {
            setError('Kunne ikke oppdatere eier: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (window.confirm(`Er du sikker på at du vil slette eieren "${initialData.name}"?`)) {
            setLoading(true);
            setError(null);
            try {
                await callApi('deleteOwnerV2', { ownerId: initialData.id });
                if (onFinished) onFinished();
            } catch (err) {
                setError('Kunne ikke slette eier: ' + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <form onSubmit={handleUpdate} style={{ background: 'var(--background-light)', color: 'var(--text-light)', padding: '20px', borderRadius: '5px', display: 'flex', flexDirection: 'column', gap: '15px', minWidth: '350px', maxWidth: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
                <h4>Rediger Eier</h4>
                <div>
                  <label htmlFor="edit-owner-name" style={{ display: 'block', marginBottom: '5px' }}>Navn på eier</label>
                  <input id="edit-owner-name" value={name} onChange={e => setName(e.target.value)} required />
                </div>
                <div>
                  <label htmlFor="edit-owner-orgnr" style={{ display: 'block', marginBottom: '5px' }}>Organisasjonsnummer (valgfritt)</label>
                  <input id="edit-owner-orgnr" value={orgnr} onChange={e => setOrgnr(e.target.value)} />
                </div>
                {error && <p style={{ color: 'red' }}>Failed to edit, 0 occurrences found for old_string (// ... EditOwnerForm remains largely the same ...). Original old_string was (// ... EditOwnerForm remains largely the same ...) in C:\Users\roars\Bifrost\bifrostkey\web\admin-panel\src\SystemAdminDashboard.js. No edits made. The exact text in old_string was not found. Ensure you're not escaping content incorrectly and check whitespace, indentation, and context. Use read_file tool to verify.</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                    <button type="button" onClick={handleDelete} disabled={loading} style={{backgroundColor: '#ff4d4d'}}>Slett</button>
                    <div>
                        <button type="button" onClick={onFinished} style={{marginRight: '10px'}}>Avbryt</button>
                        <button type="submit" disabled={loading}>{loading ? 'Lagrer...' : 'Lagre'}</button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function OwnersView({ onDrillDown, filterType = 'enterprise' }) {
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchOwners = useCallback(async () => {
    setError(null);
    try {
      const result = await callApi('listOwnersV2');
      console.log("DEBUG: Alle eiere fra backend:", result.owners); // DEBUG
      
      // Filter based on type
      const filtered = result.owners.filter(o => {
          const type = o.type || 'enterprise';
          console.log(`DEBUG: Eier ${o.name}, type: ${o.type}, treated as: ${type}, filter: ${filterType}`); // DEBUG
          return type === filterType;
      });
      setOwners(filtered);
    } catch (err) {
      setError('Kunne ikke hente liste. ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    setLoading(true);
    fetchOwners();
  }, [fetchOwners]);

  const handleFormFinished = () => {
      setShowCreateForm(false);
      setEditingOwner(null);
      fetchOwners();
  }

  return (
    <div>
      <h3>{filterType === 'private' ? 'Privatmarked (B2C)' : 'Bedrifter & Borettslag (B2B)'}</h3>
      <p>
          {filterType === 'private' 
            ? 'Oversikt over privatpersoner som eier egne porter.' 
            : 'Administrer organisasjoner, bedrifter og borettslag.'}
      </p>
      
      {!showCreateForm && !editingOwner && (
        <button onClick={() => setShowCreateForm(true)} style={{ margin: '20px 0' }}>
          + Opprett Ny {filterType === 'private' ? 'Privatperson' : 'Eier'}
        </button>
      )}
      {showCreateForm && (
        <CreateOwnerForm 
          onOwnerCreated={handleFormFinished}
          onCancel={() => setShowCreateForm(false)}
          initialType={filterType}
        />
      )}
      {editingOwner && (
          <EditOwnerForm 
            initialData={editingOwner}
            onFinished={handleFormFinished}
          />
      )}

      <h4>{filterType === 'private' ? 'Registrerte Privatkunder' : 'Eksisterende Bedriftskunder'}</h4>
      {loading && <p>Laster...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Navn</th>
              {filterType === 'enterprise' && !isMobile && <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Org.nr</th>}
              <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left', width: '1%', whiteSpace: 'nowrap' }}>Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {owners.map(owner => (
              <tr key={owner.id}>
                <td style={{ border: '1px solid #ddd', padding: '8px' }}>{owner.name}</td>
                {filterType === 'enterprise' && !isMobile && <td style={{ border: '1px solid #ddd', padding: '8px' }}>{owner.orgnr || 'N/A'}</td>}
                <td style={{ border: '1px solid #ddd', padding: '8px', width: '1%', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setEditingOwner(owner)}>Rediger</button>
                    <button onClick={() => onDrillDown(owner, 'owner')}>
                        {filterType === 'private' ? 'Administrer Porter' : 'Lokasjoner'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function SystemAdminDashboard({ onDrillDown }) {
  const [view, setView] = useState('owners_b2b'); // 'owners_b2b', 'owners_b2c', 'admins', 'devices', 'logs'
  const [allDevices, setAllDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchAllDevices = useCallback(async () => {
    setDevicesLoading(true);
    try {
      const result = await callApi('listAllDevicesV2');
      setAllDevices(result.devices);
    } catch (error) {
      console.error("Failed to fetch all devices:", error);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'devices') {
      fetchAllDevices();
    }
  }, [view, fetchAllDevices]);

  // Active: Dark background (like the header/cards), White text, Cyan border to show selection
  const activeStyle = { 
    backgroundColor: 'var(--background-light)', 
    color: 'var(--text-light)', 
    border: '1px solid var(--primary-accent)', 
    fontWeight: 'bold' 
  };
  
  // Inactive: Bright background (Cyan), Dark text - makes them pop as "clickable"
  const inactiveStyle = { 
    backgroundColor: 'var(--primary-accent)', 
    color: 'var(--text-dark)', 
    border: '1px solid var(--primary-accent)' 
  };

  const navContainerStyle = {
    marginBottom: '20px',
    display: 'flex',
    gap: '10px',
    flexDirection: isMobile ? 'column' : 'row',
    flexWrap: 'wrap'
  };

  return (
    <div>
      <h2>System Admin Dashboard</h2>
      
      <div style={navContainerStyle}>
        <button onClick={() => setView('owners_b2b')} style={view === 'owners_b2b' ? activeStyle : inactiveStyle}>Bedrifter (B2B)</button>
        <button onClick={() => setView('owners_b2c')} style={view === 'owners_b2c' ? activeStyle : inactiveStyle}>Privatmarked (B2C)</button>
        <button onClick={() => setView('admins')} style={view === 'admins' ? activeStyle : inactiveStyle}>Admins</button>
        <button onClick={() => setView('accessRequests')} style={view === 'accessRequests' ? activeStyle : inactiveStyle}>Tilgangsforespørsler</button>
        <button onClick={() => setView('devices')} style={view === 'devices' ? activeStyle : inactiveStyle}>Enheter</button>
        <button onClick={() => setView('logs')} style={view === 'logs' ? activeStyle : inactiveStyle}>Logger</button>
        <button onClick={() => setView('orphanedUsers')} style={view === 'orphanedUsers' ? activeStyle : inactiveStyle}>Inaktive</button>
      </div>

      {view === 'owners_b2b' && <OwnersView onDrillDown={onDrillDown} filterType="enterprise" />}
      {view === 'owners_b2c' && <OwnersView onDrillDown={onDrillDown} filterType="private" />}
      {view === 'admins' && <AdminManagement onViewUsers={(user) => onDrillDown(user, 'user_L2')} />}
      {view === 'accessRequests' && <AccessRequestsDashboard ownerId={null} onClose={() => setView('owners_b2b')} />}
      {view === 'devices' && <DeviceRegistry allDevices={allDevices} devicesLoading={devicesLoading} refetchAllDevices={fetchAllDevices} />}
      {view === 'logs' && <ActivityLog />}
      {view === 'orphanedUsers' && <OrphanedUsersDashboard />}
      
      <div style={{ position: 'fixed', bottom: 5, right: 5, fontSize: '10px', color: 'gray' }}>v2.1 DEBUG</div>
    </div>
  );
}

export default SystemAdminDashboard;